import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  AssessmentEvent,
  PaginatableAssessmentHistoryArgs,
  ProgramMembership
} from "../../API";
import { useAssessmentHistory, useMilestoneReport } from "./reportUtils";
import { format, isSameDay } from "date-fns";
import {
  DataGridPremium,
  GridColDef,
  GridEventListener,
  gridClasses,
  GridToolbar,
  GridTreeNodeWithRender,
  GridValueGetterParams,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
  GridColumnVisibilityModel,
  GridCsvGetRowsToExportParams,
  gridFilteredSortedRowIdsSelector,
  gridRowTreeSelector,
  GridCsvExportOptions
} from "@mui/x-data-grid-premium";
import { APIError } from "../../common-ui/APIError";
import { TableLoadingOverlay } from "../../common-ui/TableLoadingOverlay";
import { GroupingMode } from "../../app/types";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { parse } from "papaparse";

type MilestoneReportRow = {
  activitytitle?: string;
  activityeventdate?: string;
  answer?: string;
  assessmenteventid?: string;
  completiondate?: string;
  milestonename?: string;
  milestoneid?: string;
  milestonerating?: string;
  milestonescale?: string;
  milestoneshortname?: string;
  programname?: string;
};

type AssessmentEventWithOptionalMilestoneInfo = AssessmentEvent &
  MilestoneReportRow;

export const CCCAssessmentEventTable = ({
  programMembership,
  fileName,
  fromDate,
  toDate,
  groupingMode
}: {
  programMembership?: ProgramMembership | null;
  fileName?: string;
  fromDate: Date | null;
  toDate: Date | null;
  groupingMode: GroupingMode;
}) => {
  const { t } = useTranslation();
  const [assessmentHistoryArgs, setAssessmentHistoryArgs] =
    useState<PaginatableAssessmentHistoryArgs>();
  const {
    assessmentEvents,
    status: assessmentEventsStatus,
    error: assessmentEventsError
  } = useAssessmentHistory(assessmentHistoryArgs);
  const {
    milestoneReport,
    error: milestoneReportError,
    status: milestoneReportStatus
  } = useMilestoneReport(programMembership?.id ? [programMembership?.id] : []);
  const nonMilestoneGroupingModes = ["activity", "none"];
  const tableInGroupedMode = ["activity", "milestone"].includes(groupingMode);
  const [columnsToUse, setColumnsToUse] = useState<GridColDef[]>([]);
  const [rowsToDisplayInDateRange, setRowsToDisplayInDateRange] = useState<
    AssessmentEventWithOptionalMilestoneInfo[]
  >([]);
  const [colsToExport, setColsToExport] = useState<string[]>([]);

  const noGroupingColumnVisibilityModel = {
    firstName: false,
    lastName: false,
    pgy: false
  };

  const groupByActivityColumnVisibilityModel = {
    activityTitle: false,
    firstName: false,
    lastName: false,
    pgy: false
  };

  const groupByMilestoneColumnVisibilityModel = {
    milestone: false,
    firstName: false,
    lastName: false,
    pgy: false
  };

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(noGroupingColumnVisibilityModel);

  const apiRef = useGridApiRef();

  const milestoneBaseColsToExport = [
    "firstName",
    "lastName",
    "pgy",
    "milestone",
    "milestoneRating",
    "activitytitle",
    "activityeventdate"
  ];

  const activityBaseColsToExport = [
    "firstName",
    "lastName",
    "pgy",
    "activityTitle",
    "dateOfActivity",
    "rater"
  ];

  const groupByActivityInitialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: {
        model: ["activityTitle"]
      },
      sorting: {
        sortModel: [{ field: "dateOfActivity", sort: "desc" }]
      },
      columns: {
        columnVisibilityModel: groupByActivityColumnVisibilityModel
      }
    }
  });
  const groupByMilestoneInitialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: {
        model: ["milestoneTitle"]
      },
      sorting: {
        sortModel: [{ field: "dateOfActivity", sort: "desc" }]
      },
      columns: {
        columnVisibilityModel: groupByMilestoneColumnVisibilityModel
      }
    }
  });
  const noGroupInitialState: GridInitialStatePremium =
    useKeepGroupedColumnsHidden({
      apiRef,
      initialState: {
        rowGrouping: {
          model: []
        },
        sorting: {
          sortModel: [{ field: "dateOfActivity", sort: "desc" }]
        },
        columns: {
          columnVisibilityModel: noGroupingColumnVisibilityModel
        }
      }
    });

  const identityInfo: GridColDef[] = [
    {
      field: "firstName",
      headerName: "First Name",
      valueGetter: () => {
        return programMembership?.programMember?.firstName;
      }
    },
    {
      field: "lastName",
      headerName: "Last Name",
      valueGetter: () => {
        return programMembership?.programMember?.lastName;
      }
    },
    {
      field: "pgy",
      headerName: "PGY",
      valueGetter: (params) => {
        return params.row.subject?.currentPGY ?? "N/A";
      }
    }
  ];

  const milestoneColumns: GridColDef[] = [
    ...identityInfo,
    {
      field: "milestoneTitle",
      headerName: t("Milestone"),
      groupingValueGetter: (params) =>
        `${params.row.milestoneshortname} - ${params.row.milestonename}`,
      renderCell: (params) => {
        const whiteSpaceMode = ["activity", "milestone"].includes(groupingMode)
          ? "nowrap"
          : "initial";
        return (
          <span style={{ whiteSpace: whiteSpaceMode }}>{params.value}</span>
        );
      }
    },
    {
      field: "milestone",
      headerName: t("Milestone"),
      valueGetter: (params) => {
        const isParent = nodeIsParent(params);
        const toDisplay =
          params.row.milestoneshortname && params.row.milestonename
            ? `${params.row.milestoneshortname} - ${params.row.milestonename}`
            : "N/A";
        return isParent && tableInGroupedMode ? undefined : toDisplay;
      }
    },
    {
      field: "milestoneRating",
      headerName: t("Milestone Rating"),
      flex: 1,
      valueGetter: (params) => {
        const isParent = nodeIsParent(params);
        const toDisplay = params.row.milestonerating || "N/A";
        return isParent && tableInGroupedMode ? undefined : toDisplay;
      },
      renderCell: (params) => {
        return (
          <span style={{ textAlign: "center", width: "100%" }}>
            {params.value}
          </span>
        );
      }
    },
    {
      field: "activitytitle",
      headerName: t("Activity Name"),
      flex: 1
    },
    {
      field: "dateOfActivity",
      headerName: t("Date Activity Performed"),
      valueGetter: (params) => {
        const date = params.row.activityEvent?.activityEventDate;
        const isParent = nodeIsParent(params);
        return date
          ? format(new Date(date), `Ppppp`)
          : isParent
          ? undefined
          : "N/A";
      },
      flex: 1
    }
  ];

  const activityColumns: GridColDef<AssessmentEvent>[] = [
    ...identityInfo,
    {
      field: "activityTitle",
      headerName: t("Activity Name"),
      flex: 2,
      groupingValueGetter: (params) => params.row.activity?.title,
      valueGetter: (params) => params.row.activity?.title || "N/A",
      renderCell: (params) => {
        const standardStyle: React.CSSProperties = {
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis"
        };
        const parentStyle: React.CSSProperties = {
          whiteSpace: "nowrap"
        };
        return (
          <span
            style={groupingMode === "activity" ? parentStyle : standardStyle}
          >
            {params.value}
          </span>
        );
      }
    },
    {
      field: "dateOfActivity",
      headerName: t("Date Activity Performed"),
      valueGetter: (params) => {
        const date = params.row.activityEvent?.activityEventDate;
        const isParent = nodeIsParent(params);
        return date
          ? format(new Date(date), `Ppppp`)
          : isParent
          ? undefined
          : "N/A";
      },
      flex: 1
    },
    {
      field: "rater",
      headerName: t("Rater"),
      valueGetter: (params) => {
        let raterString = "N/A";
        const rater = params.row.rater?.user;
        const subject = params.row.subject?.user;
        if (rater) {
          raterString = `${rater.firstName || "N/A"} ${
            rater.lastName || "N/A"
          }`;
          if (rater.id === subject?.id) {
            raterString += " (self)";
          }
        }
        const isParent = nodeIsParent(params);
        return isParent && tableInGroupedMode ? undefined : raterString;
      },
      flex: 1
    }
  ];

  const onRowClick = useCallback<GridEventListener<"rowClick">>(
    (params) => {
      const rowNode = apiRef.current.getRowNode(params.id);
      if (rowNode && rowNode.type === "group") {
        apiRef.current.setRowChildrenExpansion(
          params.id,
          !rowNode.childrenExpanded
        );
      }
    },
    [apiRef]
  );

  const attachAssessmentsToMilestones = (
    parsedMilestoneReport: MilestoneReportRow[],
    assessmentEvents: AssessmentEvent[]
  ) => {
    const newMilestoneRows: AssessmentEventWithOptionalMilestoneInfo[] = [];
    for (const row of parsedMilestoneReport) {
      const assessmentEventId = row.assessmenteventid;
      const matchingAssessmentEvent = assessmentEvents.find(
        (assessmentEvent) => assessmentEvent.id === assessmentEventId
      );
      if (matchingAssessmentEvent) {
        const newMilestoneRow = {
          ...matchingAssessmentEvent,
          ...row,
          id: `${row.milestoneid}-${row.assessmenteventid}`
        };
        newMilestoneRows.push(newMilestoneRow);
      }
    }
    return newMilestoneRows;
  };

  const filterRowsInDateRange = (
    rowsToUse: AssessmentEventWithOptionalMilestoneInfo[],
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    if (fromDate && toDate) {
      const newAssessmentEventsInDateRange = rowsToUse?.filter((row) => {
        const activityEventDate = row.activityEvent?.activityEventDate;
        if (!activityEventDate) return false;
        const activityEventDateDate = new Date(activityEventDate);
        const activityEventDateIsInRange =
          fromDate <= activityEventDateDate && toDate >= activityEventDateDate;
        const activityIsSameDay =
          isSameDay(fromDate, activityEventDateDate) ||
          isSameDay(toDate, activityEventDateDate);
        // Exclude rows with "Demographic" in the activity title
        const isDemographic = row.activity?.title
          ?.toLowerCase()
          .includes("demographic");
        return (
          (activityEventDateIsInRange || activityIsSameDay) && !isDemographic
        );
      });
      setRowsToDisplayInDateRange(newAssessmentEventsInDateRange);
      return;
    }
    setRowsToDisplayInDateRange(
      rowsToUse.filter(
        (row) => !row.activity?.title?.toLowerCase().includes("demographic")
      )
    );
  };

  const EXCLUDED_QUESTION_TITLES = new Set([
    "Race",
    "Gender",
    "Race/Ethnicity",
    "Ethnicity"
  ]);

  const updateColumns = (
    assessmentEvents: AssessmentEvent[],
    baseColumns: GridColDef[],
    setColumnsToUse: Dispatch<SetStateAction<GridColDef[]>>,
    baseColsToExport: string[]
  ) => {
    if (assessmentEvents && assessmentEvents.length > 0) {
      const currentColumns = [...baseColumns];
      const newColsToExport = [...baseColsToExport];
      const questionMap = new Map<
        string,
        { sortingIndex: number; answer: NonNullable<AssessmentEvent["answers"]>["items"][number] }
      >();
      for (const assessmentEvent of assessmentEvents) {
        const answersInAssessmentEvent = assessmentEvent.answers?.items;
        if (answersInAssessmentEvent && answersInAssessmentEvent.length > 0) {
          for (const answer of answersInAssessmentEvent) {
            const questionAssociatedWithAnswer = answer?.question;
            const questionTitle = questionAssociatedWithAnswer?.title;
            if (
              !questionTitle ||
              EXCLUDED_QUESTION_TITLES.has(questionTitle) ||
              questionMap.has(questionTitle)
            ) {
              continue;
            }
            questionMap.set(questionTitle, {
              sortingIndex: questionAssociatedWithAnswer?.sortingIndex ?? 999,
              answer
            });
          }
        }
      }
      const sortedQuestions = [...questionMap.values()].sort(
        (a, b) => a.sortingIndex - b.sortingIndex
      );
      for (const { answer } of sortedQuestions) {
        const questionTitle = answer?.question?.title;
        const associatedColumnInTable = currentColumns.find(
          (column) => column.field === questionTitle
        );
        if (questionTitle && !associatedColumnInTable) {
              // This question doesn't have a column yet in the table. Need to add one.
              const columnToAdd: GridColDef<AssessmentEvent> = {
                field: questionTitle,
                headerName: t(questionTitle),
                valueGetter: (params) => {
                  const isParent = nodeIsParent(params);
                  const answerItems = params.row.answers?.items;
                  const answerAssociatedWithQuestion = answerItems?.find(
                    (answer) => answer?.question?.title === questionTitle
                  );
                  const value = answerAssociatedWithQuestion?.value;
                  const valueStr = (value != null ? String(value) : "").trim();
                  const answerOptions =
                    answerAssociatedWithQuestion?.question?.answerOptions
                      ?.items;
                  if (answerOptions && answerOptions.length > 0) {
                    const numVal = Number(value);
                    if (valueStr !== "" && !isNaN(numVal)) {
                      const correspondingAnswerAnchor = answerOptions.find(
                        (o) => String(o?.value ?? "").trim() === valueStr
                      )?.anchor;
                      return isParent && tableInGroupedMode
                        ? undefined
                        : correspondingAnswerAnchor ?? valueStr;
                    }
                    return isParent && tableInGroupedMode ? undefined : valueStr || value;
                  }
                  return valueStr || value
                    ? valueStr || value
                    : isParent && tableInGroupedMode
                    ? undefined
                    : "N/A";
                },
                flex: questionTitle === "Feedback" ? 2 : 1
              };
              currentColumns.push(columnToAdd);
              newColsToExport.push(columnToAdd.field);
            }
          }
      setColumnsToUse(currentColumns);
      setColsToExport(newColsToExport);
    }
  };

  useEffect(() => {
    if (programMembership) {
      setAssessmentHistoryArgs({
        programMemberships: [programMembership.id],
        membersAssessmentsOnly: true,
        limit: 100
      });
    }
  }, [programMembership]);

  useEffect(() => {
    const parsedMilestoneReport = milestoneReport
      ? parse(milestoneReport, { header: true }).data
      : undefined;
    const baseColumns =
      groupingMode === "milestone" ? milestoneColumns : activityColumns;

    const baseColsToExport =
      groupingMode === "milestone"
        ? milestoneBaseColsToExport
        : activityBaseColsToExport;

    updateColumns(
      assessmentEvents,
      baseColumns,
      setColumnsToUse,
      baseColsToExport
    );

    let milestoneReportWithAssessments;
    if (parsedMilestoneReport && assessmentEvents) {
      milestoneReportWithAssessments = attachAssessmentsToMilestones(
        parsedMilestoneReport as MilestoneReportRow[],
        assessmentEvents
      );
    }
    const sortedMilestonesWithAssessments =
      milestoneReportWithAssessments?.sort((a, b) =>
        (a.milestoneshortname || "N/A").localeCompare(
          b.milestoneshortname || "N/A"
        )
      );

    // Doing this filter on the frontend for now to not mess up any implementation
    // the mobile app might have using this API call.
    // We should revisit the completedAssessmentHistoryByProgramMembership call as a team.
    const assessmentEventsMinusSelf = assessmentEvents.filter(
      (assessmentEvent) =>
        assessmentEvent.subjectUserId !== assessmentEvent.raterUserId
    );
    const sortedAssessmentsMinusSelf = assessmentEventsMinusSelf.sort((a, b) =>
      (a.activity?.title || "N/A").localeCompare(b.activity?.title || "N/A")
    );

    let initialRowsToDisplay;

    if (apiRef.current) {
      if (groupingMode === "milestone") {
        apiRef.current.restoreState(groupByMilestoneInitialState);
        if (sortedMilestonesWithAssessments) {
          initialRowsToDisplay = sortedMilestonesWithAssessments;
        }
      } else {
        if (groupingMode === "none") {
          apiRef.current.restoreState(noGroupInitialState);
        }
        if (groupingMode === "activity") {
          apiRef.current.restoreState(groupByActivityInitialState);
        }
        initialRowsToDisplay = sortedAssessmentsMinusSelf;
      }
    }

    if (initialRowsToDisplay) {
      filterRowsInDateRange(initialRowsToDisplay, fromDate, toDate);
    }
  }, [assessmentEvents, groupingMode, milestoneReport, fromDate, toDate]);

  const csvOptions: GridCsvExportOptions = {
    fileName: fileName || "report",
    allColumns: true,
    includeColumnGroupsHeaders: false,
    fields: colsToExport,
    getRowsToExport: (params: GridCsvGetRowsToExportParams) => {
      const { apiRef } = params;
      const rows = gridFilteredSortedRowIdsSelector(apiRef);
      const tree = gridRowTreeSelector(apiRef);
      return rows.filter((rowId) => tree[rowId].type !== "group");
    }
  };

  const nodeIsParent = (
    params: GridValueGetterParams<AssessmentEvent, any, GridTreeNodeWithRender>
  ) => {
    return params.rowNode.parent?.toString().includes("root");
  };

  if (assessmentEventsError)
    return <APIError error={JSON.stringify(assessmentEventsError)} />;

  if (groupingMode === "milestone" && milestoneReportError)
    return <APIError error={JSON.stringify(milestoneReportError)} />;

  return (
    <DataGridPremium
      aria-label="assessment-events-table"
      getRowHeight={() => "auto"}
      apiRef={apiRef}
      initialState={noGroupInitialState}
      sx={{
        [`& .${gridClasses.cell}`]: {
          py: 1
        }
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(newModel) =>
        setColumnVisibilityModel(newModel)
      }
      defaultGroupingExpansionDepth={-1}
      onRowClick={onRowClick}
      groupingColDef={{
        hideDescendantCount: true
      }}
      disableRowSelectionOnClick={true}
      rows={rowsToDisplayInDateRange}
      columns={columnsToUse}
      loading={
        (nonMilestoneGroupingModes.includes(groupingMode) &&
          assessmentEventsStatus === "loading") ||
        (groupingMode === "milestone" && milestoneReportStatus === "loading")
      }
      slots={{
        loadingOverlay: TableLoadingOverlay,
        toolbar: GridToolbar
      }}
      slotProps={{
        toolbar: {
          csvOptions: csvOptions,
          excelOptions: csvOptions
        },
        loadingOverlay: {
          message: "Fetching assessments..."
        }
      }}
    />
  );
};
